import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/TranslationContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Download, Upload, Archive, RefreshCw, Trash2, ChevronDown, ChevronUp,
  CheckCircle2, AlertCircle, Clock, FileArchive, HardDrive, Loader2,
} from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

// ---------------------------------------------------------------------------
// Collection registry (must stay in sync with backend ALL_COLLECTIONS)
// ---------------------------------------------------------------------------

const COLLECTIONS = [
  { id: 'courses',           label: 'Courses' },
  { id: 'lessons',           label: 'Lessons' },
  { id: 'quizzes',           label: 'Quizzes' },
  { id: 'users',             label: 'Users' },
  { id: 'coupons',           label: 'Coupons' },
  { id: 'platform_settings', label: 'Settings' },
  { id: 'roles',             label: 'Roles' },
  { id: 'categories',        label: 'Categories' },
  { id: 'live_classes',      label: 'Live Classes' },
  { id: 'projects',          label: 'Projects' },
  { id: 'affiliates',        label: 'Affiliates' },
  { id: 'share_rewards',     label: 'Share Rewards' },
];

const ALL_IDS = COLLECTIONS.map((c) => c.id);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function fmtBytes(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function actionBadgeVariant(action) {
  if (action === 'export' || action === 'auto_export') return 'default';
  if (action === 'import') return 'secondary';
  return 'outline';
}

function statusBadge(status) {
  if (status === 'success') return <Badge className="bg-green-100 text-green-800">Success</Badge>;
  if (status === 'partial') return <Badge className="bg-yellow-100 text-yellow-800">Partial</Badge>;
  if (status === 'failed')  return <Badge variant="destructive">Failed</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const AdminBackup = () => {
  const { token, isSuperAdmin } = useAuth();
  const { t } = useTranslation();
  const headers = { Authorization: `Bearer ${token}` };

  // ── Export state ──────────────────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [selected, setSelected] = useState(new Set(ALL_IDS));

  // ── Import state ──────────────────────────────────────────────────────────
  const [importFile, setImportFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);  // { import_id, manifest, preview, default_actions }
  const [previewOpen, setPreviewOpen] = useState(false);
  const [actions, setActions] = useState({});             // { collectionName: 'merge'|'replace'|'skip' }
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  // ── Logs & files state ────────────────────────────────────────────────────
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [clearingLogs, setClearingLogs] = useState(false);
  const [autoFiles, setAutoFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);

  // ── Auto-backup settings ──────────────────────────────────────────────────
  const [autoSettings, setAutoSettings] = useState({
    auto_backup_enabled: false,
    auto_backup_frequency: 'daily',
  });
  const [savingAuto, setSavingAuto] = useState(false);

  // ── Load on mount ─────────────────────────────────────────────────────────
  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/backup/logs`, { headers });
      setLogs(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Failed to load backup logs');
    } finally {
      setLogsLoading(false);
    }
  }, [token]);

  const loadAutoFiles = useCallback(async () => {
    setFilesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/backup/files`, { headers });
      setAutoFiles(Array.isArray(res.data) ? res.data : []);
    } catch {
      setAutoFiles([]);
    } finally {
      setFilesLoading(false);
    }
  }, [token]);

  const loadAutoSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/settings`, { headers });
      const s = res.data || {};
      setAutoSettings({
        auto_backup_enabled: s.auto_backup_enabled || false,
        auto_backup_frequency: s.auto_backup_frequency || 'daily',
      });
    } catch { /* settings may not have these fields yet — defaults above */ }
  }, [token]);

  useEffect(() => {
    loadLogs();
    loadAutoFiles();
    loadAutoSettings();
  }, [loadLogs, loadAutoFiles, loadAutoSettings]);

  // ── Export ────────────────────────────────────────────────────────────────

  const handleExport = useCallback(async (collections) => {
    setExporting(true);
    try {
      const res = await axios.post(
        `${API_URL}/admin/backup/export`,
        { collections },
        { headers, responseType: 'blob' },
      );
      const disposition = res.headers['content-disposition'] || '';
      const fnMatch = disposition.match(/filename="([^"]+)"/);
      const filename = fnMatch ? fnMatch[1] : `thachari-backup-${Date.now()}.zip`;
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
      loadLogs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [token]);

  const toggleCollection = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(selected.size === ALL_IDS.length ? new Set() : new Set(ALL_IDS));
  };

  // ── Import: file selection ────────────────────────────────────────────────

  const startPreview = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast.error('Please select a .zip backup file');
      return;
    }
    setImportFile(file);
    setPreviewing(true);
    setImportResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API_URL}/admin/backup/import`, form, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setPreviewData(res.data);
      setActions(res.data.default_actions || {});
      setPreviewOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to read backup file');
      setImportFile(null);
    } finally {
      setPreviewing(false);
    }
  }, [token]);

  // Drag-and-drop handlers
  const onDragOver = (e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); };
  const onDragLeave = (e) => { e.currentTarget.classList.remove('border-primary'); };
  const onDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-primary');
    const file = e.dataTransfer.files?.[0];
    if (file) startPreview(file);
  };

  // ── Import: confirm ───────────────────────────────────────────────────────

  const handleConfirmImport = async () => {
    if (!previewData?.import_id) return;
    setImporting(true);
    try {
      const res = await axios.post(
        `${API_URL}/admin/backup/import/confirm`,
        { import_id: previewData.import_id, actions },
        { headers },
      );
      setImportResult(res.data);
      setPreviewOpen(false);
      setImportFile(null);
      setPreviewData(null);
      toast.success(
        res.data.status === 'success'
          ? 'Import completed successfully'
          : 'Import completed with warnings',
      );
      loadLogs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // ── Logs ──────────────────────────────────────────────────────────────────

  const handleClearLogs = async () => {
    if (!window.confirm('Clear all backup logs? This cannot be undone.')) return;
    setClearingLogs(true);
    try {
      await axios.delete(`${API_URL}/admin/backup/logs`, { headers });
      toast.success('Logs cleared');
      setLogs([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to clear logs');
    } finally {
      setClearingLogs(false);
    }
  };

  // ── Auto-backup settings ──────────────────────────────────────────────────

  const handleSaveAutoSettings = async () => {
    setSavingAuto(true);
    try {
      await axios.put(`${API_URL}/admin/settings`, autoSettings, { headers });
      toast.success('Auto-backup settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingAuto(false);
    }
  };

  const handleDownloadAutoFile = async (filename) => {
    try {
      const res = await axios.get(
        `${API_URL}/admin/backup/files/${encodeURIComponent(filename)}/download`,
        { headers, responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download file');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const lastImport = logs.find((l) => l.action === 'import');

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Backup & Restore</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Export platform data as a ZIP archive or restore from a previous backup.
        </p>
      </div>

      {/* ── Top row: Export + Import ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── Export card ── */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Archive className="w-4 h-4" />
              Export
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              className="w-full"
              onClick={() => handleExport(ALL_IDS)}
              disabled={exporting}
            >
              {exporting
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Exporting…</>
                : <><Download className="w-4 h-4 mr-2" /> Export Everything</>}
            </Button>

            {/* Custom Export collapsible */}
            <div className="border rounded-md">
              <button
                type="button"
                onClick={() => setCustomOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors rounded-md"
              >
                <span>Custom Export</span>
                {customOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {customOpen && (
                <div className="px-4 pb-4 space-y-3 border-t mt-0">
                  {/* Select All toggle */}
                  <div className="flex items-center gap-2 pt-3">
                    <Checkbox
                      id="sel-all"
                      checked={selected.size === ALL_IDS.length}
                      onCheckedChange={toggleAll}
                    />
                    <Label htmlFor="sel-all" className="text-xs font-medium cursor-pointer">
                      {selected.size === ALL_IDS.length ? 'Deselect All' : 'Select All'}
                    </Label>
                  </div>

                  {/* Collection grid */}
                  <div className="grid grid-cols-2 gap-2">
                    {COLLECTIONS.map((c) => (
                      <div key={c.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`sel-${c.id}`}
                          checked={selected.has(c.id)}
                          onCheckedChange={() => toggleCollection(c.id)}
                        />
                        <Label htmlFor={`sel-${c.id}`} className="text-xs cursor-pointer">
                          {c.label}
                        </Label>
                      </div>
                    ))}
                  </div>

                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    disabled={selected.size === 0 || exporting}
                    onClick={() => handleExport(Array.from(selected))}
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Export Selected ({selected.size})
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ── Import card ── */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Upload className="w-4 h-4" />
              Import
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Drop zone */}
            <div
              ref={dropRef}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary hover:bg-muted/30 transition-colors"
            >
              {previewing ? (
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-8 h-8 animate-spin" />
                  <span className="text-sm">Reading backup file…</span>
                </div>
              ) : importFile ? (
                <div className="flex flex-col items-center gap-2">
                  <FileArchive className="w-8 h-8 text-primary" />
                  <span className="text-sm font-medium">{importFile.name}</span>
                  <span className="text-xs text-muted-foreground">{fmtBytes(importFile.size)}</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <Upload className="w-8 h-8" />
                  <span className="text-sm">Drag a .zip backup here or click to browse</span>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) startPreview(f); e.target.value = ''; }}
            />

            <p className="text-xs text-muted-foreground">
              Last import:{' '}
              {lastImport ? fmtDate(lastImport.timestamp) : 'Never'}
            </p>

            {/* Import result summary */}
            {importResult && (
              <div className="rounded-md border p-3 space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  {importResult.status === 'success'
                    ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                    : <AlertCircle className="w-4 h-4 text-yellow-600" />}
                  Import {importResult.status}
                </div>
                {importResult.warnings?.length > 0 && (
                  <ul className="text-xs text-muted-foreground space-y-0.5 pl-4 list-disc">
                    {importResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                )}
                <div className="text-xs text-muted-foreground">
                  {Object.entries(importResult.results || {}).map(([name, r]) => (
                    <span key={name} className="mr-3">
                      {name}: +{r.inserted} / skipped {r.skipped}
                      {r.errors?.length > 0 && <span className="text-red-500"> ({r.errors.length} err)</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Preview / Confirm modal ── */}
      <Dialog open={previewOpen} onOpenChange={(v) => { if (!importing) setPreviewOpen(v); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Preview</DialogTitle>
          </DialogHeader>

          {previewData && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Backup exported{' '}
                {fmtDate(previewData.manifest?.export_date)} ·{' '}
                v{previewData.manifest?.version}
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Collection</TableHead>
                    <TableHead className="text-right">In Backup</TableHead>
                    <TableHead className="text-right">In DB Now</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(previewData.preview || {}).map(([name, counts]) => {
                    const label = COLLECTIONS.find((c) => c.id === name)?.label || name;
                    return (
                      <TableRow key={name}>
                        <TableCell className="font-medium text-sm">{label}</TableCell>
                        <TableCell className="text-right text-sm">{counts.in_backup}</TableCell>
                        <TableCell className="text-right text-sm">{counts.in_db}</TableCell>
                        <TableCell>
                          <Select
                            value={actions[name] || 'merge'}
                            onValueChange={(v) => setActions((a) => ({ ...a, [name]: v }))}
                          >
                            <SelectTrigger className="h-7 text-xs w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="merge">Merge</SelectItem>
                              <SelectItem value="replace">Replace All</SelectItem>
                              <SelectItem value="skip">Skip</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <div className="text-xs text-muted-foreground space-y-1 rounded-md bg-muted/50 p-3">
                <p><strong>Merge</strong> — inserts only records whose ID does not already exist in the database.</p>
                <p><strong>Replace All</strong> — deletes existing records first, then inserts all from the backup.</p>
                <p><strong>Skip</strong> — leaves the collection untouched.</p>
                <p className="text-yellow-700"><strong>Users:</strong> existing accounts are always skipped in Merge mode to protect passwords.</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setPreviewOpen(false); setImportFile(null); setPreviewData(null); }}
              disabled={importing}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmImport} disabled={importing}>
              {importing
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</>
                : 'Confirm Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Backup History ── */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="w-4 h-4" />
            Backup History
          </CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={loadLogs} disabled={logsLoading}>
              <RefreshCw className={`w-3 h-3 mr-1 ${logsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {isSuperAdmin && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleClearLogs}
                disabled={clearingLogs || logs.length === 0}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Clear Logs
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No backup operations recorded yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date / Time</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Collections</TableHead>
                  <TableHead className="text-right">Records</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => {
                  const totalRecords = Object.values(log.record_counts || {}).reduce((s, v) => s + (v || 0), 0);
                  return (
                    <TableRow key={log.id || log.timestamp}>
                      <TableCell className="text-xs">{fmtDate(log.timestamp)}</TableCell>
                      <TableCell className="text-xs">{log.admin_email}</TableCell>
                      <TableCell>
                        <Badge variant={actionBadgeVariant(log.action)} className="text-xs capitalize">
                          {log.action === 'auto_export' ? 'Auto Export' : log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {(log.collections || []).length} collection{log.collections?.length !== 1 ? 's' : ''}
                      </TableCell>
                      <TableCell className="text-right text-xs">{totalRecords.toLocaleString()}</TableCell>
                      <TableCell>{statusBadge(log.status)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Auto-Backup Settings ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <HardDrive className="w-4 h-4" />
            Auto-Backup Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch
              id="auto-backup-enabled"
              checked={autoSettings.auto_backup_enabled}
              onCheckedChange={(v) => setAutoSettings((s) => ({ ...s, auto_backup_enabled: v }))}
            />
            <Label htmlFor="auto-backup-enabled" className="text-sm">
              Enable automatic backups
            </Label>
          </div>

          {autoSettings.auto_backup_enabled && (
            <div className="flex items-center gap-3">
              <Label className="text-sm w-24">Frequency</Label>
              <Select
                value={autoSettings.auto_backup_frequency}
                onValueChange={(v) => setAutoSettings((s) => ({ ...s, auto_backup_frequency: v }))}
              >
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Auto-backups run at midnight UTC and are saved to the server at{' '}
            <code className="text-xs bg-muted px-1 rounded">/root/thachari/backend/backups/</code>.
            The last 7 files are kept automatically.
          </p>

          <Button size="sm" onClick={handleSaveAutoSettings} disabled={savingAuto}>
            {savingAuto ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : 'Save Settings'}
          </Button>

          {/* Auto-backup files list */}
          {autoFiles.length > 0 && (
            <div className="border rounded-md mt-2">
              <div className="px-4 py-2 flex items-center justify-between border-b">
                <span className="text-sm font-medium">Saved Auto-Backups</span>
                <Button size="sm" variant="ghost" onClick={loadAutoFiles} disabled={filesLoading}>
                  <RefreshCw className={`w-3 h-3 ${filesLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <Table>
                <TableBody>
                  {autoFiles.map((f) => (
                    <TableRow key={f.filename}>
                      <TableCell className="text-xs font-mono">{f.filename}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmtBytes(f.size_bytes)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmtDate(f.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadAutoFile(f.filename)}
                        >
                          <Download className="w-3 h-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBackup;
